/**
 * OptionCard (BLU-1376): a choice card for the onboarding wizard.
 *
 * A real `<input type="radio">` inside a `<label>`, so the whole card is the
 * hit target, arrow keys move between cards that share a `name`, and tests can
 * find each option with `getByRole('radio', { name })`. The native input is a
 * transparent overlay covering the whole card (so pointer and test clicks land
 * on the real radio); a 20px round mark stands in for it visually and draws a
 * tick when selected (`animate-check-enter`, 250ms).
 *
 * States (handover 4.3):
 * - rest: 1px `line-strong` border (#C3CBD6) on white;
 * - hover: purple-ish border;
 * - selected: brand purple border + 1px ring + brand tint fill (`bg-accent`);
 * - focus: a 2px ring when the radio has keyboard focus;
 * - disabled: 55% opacity plus an optional reason line.
 * Every transition is 150ms ease-out and off under reduced motion.
 *
 * Group it inside an element with `role="radiogroup"` and an accessible name.
 */
import { useId, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface OptionCardProps {
  /** The radio group's name; cards sharing it form one group. */
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  label: ReactNode;
  body?: ReactNode;
  disabled?: boolean;
  /** Shown under the body when the card is disabled, and read with it. */
  disabledReason?: ReactNode;
  /** Extra ids describing the radio (appended after the body). */
  describedBy?: string;
  className?: string;
}

export function OptionCard({
  name,
  value,
  checked,
  onChange,
  label,
  body,
  disabled = false,
  disabledReason,
  describedBy,
  className,
}: OptionCardProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const bodyId = `${id}-body`;
  const reasonId = `${id}-reason`;
  const showReason = disabled && disabledReason != null;
  const describedByIds =
    [body != null ? bodyId : null, showReason ? reasonId : null, describedBy ?? null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <label
      htmlFor={id}
      data-state={checked ? 'checked' : 'unchecked'}
      className={cn(
        'relative flex items-start gap-3 rounded-lg border bg-background p-3.5 text-left',
        'transition-[border-color,background-color,box-shadow] duration-150 ease-out motion-reduce:transition-none',
        'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-threadheads-purple has-[:focus-visible]:ring-offset-2',
        checked
          ? 'border-threadheads-purple bg-accent ring-1 ring-threadheads-purple'
          : 'border-line-strong',
        disabled
          ? 'cursor-not-allowed opacity-[0.55]'
          : cn('cursor-pointer', !checked && 'hover:border-threadheads-purple/60'),
        className,
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
        aria-labelledby={labelId}
        aria-describedby={describedByIds}
        className="absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none rounded-lg opacity-0 disabled:cursor-not-allowed"
      />
      <span
        aria-hidden="true"
        className={cn(
          'mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          checked ? 'border-threadheads-purple bg-threadheads-purple text-white' : 'border-line-strong bg-background',
        )}
      >
        {checked && (
          <Check className="h-3 w-3 animate-check-enter motion-reduce:animate-none" strokeWidth={3} />
        )}
      </span>
      <span className="min-w-0 space-y-1">
        <span id={labelId} className="block text-sm font-medium leading-5 text-foreground">
          {label}
        </span>
        {body != null && (
          <span id={bodyId} className="block text-xs leading-4 text-muted-foreground">
            {body}
          </span>
        )}
        {showReason && (
          <span id={reasonId} className="block text-xs leading-4 text-amber-700">
            {disabledReason}
          </span>
        )}
      </span>
    </label>
  );
}
